import { siteLocaleAtom } from "@/config.ts";
import { useInitializeAnalytics } from "@/modules/analytics/analytics.ts";
import { useRouterAnalytics } from "@/modules/analytics/useRouterAnalytics.ts";
import { routerAtom } from "@/router.store.ts";
import { createStandardRouter } from "@/router.ts";
import { type RegisteredRouter, RouterProvider } from "@tanstack/react-router";
import { makeOnce } from "@warrenio/utils/makeOnce";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import { useAtomValue } from "jotai/react";
import { useHydrateAtoms } from "jotai/utils";
import * as RA from "react-aria";
import * as RAC from "react-aria-components";

const getRouter = makeOnce(createStandardRouter);

export function AppRouterProvider() {
    return <AppRouterRoot router={getRouter()} />;
}

/**
 * Adds the router integrations that the application uses: analytics page views and react-aria link navigation.
 *
 * Use this instead of {@link AppRouterProvider} to supply your own router.
 */
export function AppRouterRoot({ router }: { router: RegisteredRouter }) {
    useHydrateAtoms([[routerAtom, router]]);

    useInitializeAnalytics();
    useRouterAnalytics(router);

    const siteLocale = useAtomValue(siteLocaleAtom);

    return (
        <RA.I18nProvider locale={siteLocale}>
            <RAC.RouterProvider
                navigate={(path, routerOptions): void => {
                    console.debug("react-aria Router navigate: %s (%o)", path, routerOptions);
                    discardPromise(router.navigate({ href: path, ...routerOptions }));
                }}
            >
                <RouterProvider router={router} />
            </RAC.RouterProvider>
        </RA.I18nProvider>
    );
}
